/**
 * Générateur — Calculer la valeur numérique d'expressions contenant des
 * puissances simples, additions, soustractions et produits.
 * Attendu ID : 930 | Niveau : 5ème | Domaine : Nombres et calculs / Puissances
 *
 * Plan de cartes (mise en conformité avec la spécification 930, campagne 5e,
 * 2026-09-22) :
 *
 *   Carte « Le bon ordre des étapes » (id etapes) — niveau 2
 *     etapes_texte_trou_saisie : le déroulé imposé par la hiérarchie, une
 *       étape par ligne (la puissance, puis le produit, puis la somme ou la
 *       différence) — c'est la correction en colonne que décrit le type_reponse.
 *     etapes_qcm_unique : le résultat, avec des propositions adossées aux
 *       erreurs nommées.
 *
 *   Carte « Avec ou sans parenthèses » (id parentheses) — niveau 3
 *     parentheses_texte_trou_saisie : deux expressions voisines calculées côte
 *       à côte, l'une avec parenthèse, l'autre sans.
 *     parentheses_tableau_bijection : trois expressions voisines à associer à
 *       leur valeur, valeurs deux à deux distinctes par construction.
 *
 *   Carte « Sommes et différences de puissances » (id sommes) — niveaux 1 et 4
 *     sommes_texte_trou_saisie : trois calculs courts, jamais deux fois le même.
 *     sommes_qcm_unique : un calcul, propositions adossées aux erreurs nommées.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\930.json) :
 *   Bornes appliquées : exposant 2 ou 3 ; base d'un carré 1..12 ; base d'un
 *   cube 1..10 ; une ou deux puissances par expression ; deux à quatre
 *   opérations (la puissance en est une) ; termes entiers 1..20 ; parenthèses
 *   jamais imbriquées ; résultat dans −200..2000, toujours positif hors du
 *   niveau 5 ; nature des nombres = entiers naturels (niveau 5 non activé).
 *   Exclusions appliquées : aucune division nulle part, pas même dans une
 *   proposition fausse ; aucun exposant nul, négatif ou supérieur à 3 ; aucun
 *   décimal ; aucun relatif, aucune base négative ; tout tirage dont le
 *   résultat sort de l'intervalle est rejoué ; aucune expression à deux
 *   lectures.
 *   Erreurs visées → mots-clés :
 *     cube lu comme le triple, carré comme le double → puissance_lue_comme_produit
 *     calcul de gauche à droite sans hiérarchie      → sans_hierarchie_gauche_droite
 *     exposant appliqué au produit tout entier       → exposant_sur_le_produit
 *     exposant distribué sur une somme               → exposant_distribue_sur_la_somme
 *     somme effectuée avant le produit               → somme_avant_produit
 *     zéros d'une puissance de 10 mal comptés        → zeros_de_la_puissance_de_10
 *     parenthèse non calculée en premier             → parenthese_non_prioritaire
 *   Graduation → cartes : niveau 1 → sommes_* (types « somme simple » et
 *   « différence », deux opérations) ; niveau 2 → etapes_* ; niveau 3 →
 *   parentheses_* ; niveau 4 → sommes_* (types à deux puissances dont un cube,
 *   jusqu'à quatre opérations) ; niveau 5 → aucune carte, la spec le dit
 *   « désactivé par défaut ».
 *   Non tranché : la borne basse de `terme_entier` (1) est appliquée aux termes
 *   additifs mais pas aux coefficients d'un produit — « 1 × 5² » n'est pas une
 *   écriture qu'on propose à un élève. La borne basse des bases (1) est
 *   appliquée telle quelle bien qu'un carré ou un cube de 1 n'apprenne rien.
 *   Les puissances sont écrites avec les exposants Unicode ² et ³, comme dans
 *   tout ce gen depuis son origine ; `points_de_vigilance` 5 demande de
 *   contrôler leur lisibilité à l'écran, ce qu'un dump texte ne permet pas.
 *
 * Arithmétique entière exacte. Aucun quotient, sous aucune forme.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, basePayload,
} from '../../../utils';

const ATTENDU_ID = 930;

const L_ETAPES = 'Le bon ordre des étapes';
const L_PARENTHESES = 'Avec ou sans parenthèses';
const L_SOMMES = 'Sommes et différences de puissances';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Bornes de la spécification ─────────────────────────────────────────
const BASE_CARRE_MIN = 1;
const BASE_CARRE_MAX = 12;
const BASE_CUBE_MIN = 1;
const BASE_CUBE_MAX = 10;
const TERME_MIN = 1;
const TERME_MAX = 20;
const COEF_MIN = 2;          // voir « non tranché » : jamais « 1 × … »
const VAL_MIN = 0;           // niveau 5 (relatifs) non activé : résultat positif
const VAL_MAX = 2000;

const SUP = { 2: '²', 3: '³' };
const puiss = (a, n) => `${a}${SUP[n]}`;
const MOINS = '−';   // U+2212, le vrai signe moins

const dansLesBornes = (v) => Number.isInteger(v) && v > VAL_MIN && v <= VAL_MAX;

/** Une puissance simple : exposant 2 ou 3, base dans les bornes de la spec. */
const tirerPuissance = (nImpose) => {
    const n = nImpose || randomChoice([2, 3]);
    const c = n === 2 ? randInt(BASE_CARRE_MIN, BASE_CARRE_MAX) : randInt(BASE_CUBE_MIN, BASE_CUBE_MAX);
    return { c, n, v: c ** n };
};

/** La valeur fausse d'une puissance de 10 : un zéro de trop (erreurs_visees 6). */
const zerosFaux = (c, n) => (c === 10 ? 10 ** (n + 1) : null);

/**
 * Trois propositions distinctes, chacune adossée à une erreur nommée. Aucune
 * option inventée à l'assemblage : si les familles coïncident, on rejoue tout
 * le tirage. Les valeurs nulles, négatives ou hors de portée sont écartées :
 * un élève les élimine à vue et la proposition ne mesure plus rien.
 */
const PLAFOND_DISTRACTEUR = 20000;
const distracteursDistincts = (correct, candidats, nb = 3) => {
    const vus = new Set([String(correct)]);
    const retenus = [];
    shuffle(candidats).forEach(d => {
        const x = Number(d.valeur);
        const s = String(d.valeur);
        if (vus.has(s) || !Number.isInteger(x) || x <= 0 || x > PLAFOND_DISTRACTEUR) return;
        vus.add(s);
        retenus.push({ code: d.code, valeur: s });
    });
    return retenus.length >= nb ? retenus.slice(0, nb) : null;
};

// ═════════ Carte 1 — Le bon ordre des étapes (niveau 2) ═════════

/**
 * Une puissance, un produit, une somme ou une différence : trois opérations,
 * aucune parenthèse (graduation_difficulte niveau 2).
 */
const tirerExpressionEtapes = () => {
    for (let essai = 0; essai < 200; essai++) {
        const { c, n, v } = tirerPuissance();
        const b = randInt(COEF_MIN, TERME_MAX);
        const forme = randomChoice(['plus', 'moinsDroite', 'moinsGauche']);
        const faux10 = zerosFaux(c, n);
        if (forme === 'plus') {
            const a = randInt(TERME_MIN, TERME_MAX);
            const resultat = a + b * v;
            if (!dansLesBornes(resultat)) continue;
            return {
                forme, c, n, v, b, a, resultat,
                affichage: `${a} + ${b} × ${puiss(c, n)}`,
                gabarits: [`${a} + ${b} × #`, `${a} + #`, '#'],
                etapes: [v, b * v, resultat],
                candidats: [
                    { code: 'somme_avant_produit', valeur: (a + b) * v },
                    { code: 'puissance_lue_comme_produit', valeur: a + b * c * n },
                    { code: 'exposant_sur_le_produit', valeur: a + (b * c) ** n },
                    ...(faux10 ? [{ code: 'zeros_de_la_puissance_de_10', valeur: a + b * faux10 }] : []),
                ],
            };
        }
        if (forme === 'moinsDroite') {
            const d = randInt(TERME_MIN, Math.min(TERME_MAX, b * v - 1));
            const resultat = b * v - d;
            if (!dansLesBornes(resultat)) continue;
            return {
                forme, c, n, v, b, d, resultat,
                affichage: `${b} × ${puiss(c, n)} ${MOINS} ${d}`,
                gabarits: [`${b} × # ${MOINS} ${d}`, `# ${MOINS} ${d}`, '#'],
                etapes: [v, b * v, resultat],
                candidats: [
                    { code: 'exposant_sur_le_produit', valeur: (b * c) ** n - d },
                    { code: 'puissance_lue_comme_produit', valeur: b * c * n - d },
                    { code: 'sans_hierarchie_gauche_droite', valeur: b * (v - d) },
                    ...(faux10 ? [{ code: 'zeros_de_la_puissance_de_10', valeur: b * faux10 - d }] : []),
                ],
            };
        }
        // a − b × cⁿ, avec a > b·cⁿ : le résultat reste positif (niveau 5 non activé)
        if (b * v >= TERME_MAX) continue;
        const a = randInt(b * v + 1, TERME_MAX);
        const resultat = a - b * v;
        if (!dansLesBornes(resultat)) continue;
        return {
            forme, c, n, v, b, a, resultat,
            affichage: `${a} ${MOINS} ${b} × ${puiss(c, n)}`,
            gabarits: [`${a} ${MOINS} ${b} × #`, `${a} ${MOINS} #`, '#'],
            etapes: [v, b * v, resultat],
            candidats: [
                { code: 'somme_avant_produit', valeur: Math.abs((a - b) * v) },
                { code: 'puissance_lue_comme_produit', valeur: a - b * c * n },
                { code: 'exposant_sur_le_produit', valeur: Math.abs(a - (b * c) ** n) },
            ],
        };
    }
    // Repli déterministe : 2 + 3 × 5²
    return {
        forme: 'plus', c: 5, n: 2, v: 25, b: 3, a: 2, resultat: 77,
        affichage: '2 + 3 × 5²',
        gabarits: ['2 + 3 × #', '2 + #', '#'],
        etapes: [25, 75, 77],
        candidats: [
            { code: 'somme_avant_produit', valeur: 125 },
            { code: 'puissance_lue_comme_produit', valeur: 32 },
            { code: 'exposant_sur_le_produit', valeur: 227 },
        ],
    };
};

const CONSIGNE_DECIMALE = '(Réponse en écriture décimale, jamais sous forme de puissance.)';

const genEtapesTrous = () => {
    const e = tirerExpressionEtapes();
    const lettre = randomChoice(['A', 'B', 'C', 'D']);
    // Une étape par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // égalités à trous, l'impression coupait la ligne au milieu).
    const phrase = [`${lettre} = ${e.affichage}`,
        ...e.gabarits.map((g, i) => `${lettre} = ${g.replace('#', `{{${e.etapes[i]}}}`)}`),
    ].join('\n');
    const payload = makeTexteTrou(phrase,
        '<p><b>Calcule étape par étape.</b> (On calcule d\'abord la puissance, '
        + `puis le produit, puis la somme ou la différence.) ${CONSIGNE_DECIMALE}</p>`,
        { ...opts(L_ETAPES), mode: 'saisie' });
    payload._meta.params = {
        affichage: e.affichage, etapes: e.etapes, resultat: e.resultat,
        c: e.c, n: e.n, b: e.b, a: e.a, d: e.d, niveau: 2, nbPuissances: 1, nbOperations: 3,
        erreurVisee: ['somme_avant_produit'],
    };
    return payload;
};

const genEtapesQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const e = tirerExpressionEtapes();
        const distracteurs = distracteursDistincts(e.resultat, e.candidats);
        if (!distracteurs) continue;
        const correct = String(e.resultat);
        const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
            `<p><b>Calcule : ${e.affichage} = ?</b> ${CONSIGNE_DECIMALE}</p>`, opts(L_ETAPES));
        payload._meta.params = {
            affichage: e.affichage, resultat: e.resultat, c: e.c, n: e.n, b: e.b,
            a: e.a, d: e.d, niveau: 2, nbPuissances: 1, nbOperations: 3, distracteurs,
        };
        return payload;
    }
    // Repli déterministe : 2 + 3 × 5²
    const distracteurs = [
        { code: 'somme_avant_produit', valeur: '125' },
        { code: 'puissance_lue_comme_produit', valeur: '32' },
        { code: 'exposant_sur_le_produit', valeur: '227' },
    ];
    const payload = makeQcm(['77', ...distracteurs.map(d => d.valeur)], '77',
        `<p><b>Calcule : 2 + 3 × 5² = ?</b> ${CONSIGNE_DECIMALE}</p>`, opts(L_ETAPES));
    payload._meta.params = {
        affichage: '2 + 3 × 5²', resultat: 77, c: 5, n: 2, b: 3, a: 2,
        niveau: 2, nbPuissances: 1, nbOperations: 3, distracteurs,
    };
    return payload;
};

// ═════════ Carte 2 — Avec ou sans parenthèses (niveau 3) ═════════

/**
 * Trois familles de couples voisins, une parenthèse au plus, jamais imbriquée
 * (variables.profondeur_de_parentheses max 1) :
 *   'produit'    b × c² ≠ (b × c)²              → exposant_sur_le_produit
 *   'somme'      (a + b)² ≠ a² + b²             → exposant_distribue_sur_la_somme
 *   'difference' b × (c² − d) ≠ b × c² − d      → parenthese_non_prioritaire
 */
const tirerParentheses = () => {
    for (let essai = 0; essai < 200; essai++) {
        const famille = randomChoice(['produit', 'somme', 'difference']);
        if (famille === 'somme') {
            const a = randInt(TERME_MIN, BASE_CARRE_MAX);
            const b = randInt(TERME_MIN, BASE_CARRE_MAX);
            if (a <= b) continue;                        // (a − b)² reste positif et distinct
            const avec = (a + b) ** 2;
            const sans = a * a + b * b;
            const tiers = (a - b) ** 2;
            if (!dansLesBornes(avec) || new Set([avec, sans, tiers]).size !== 3) continue;
            return {
                famille, a, b,
                exprAvec: `(${a} + ${b})²`, valAvec: avec,
                exprSans: `${puiss(a, 2)} + ${puiss(b, 2)}`, valSans: sans,
                exprTiers: `(${a} ${MOINS} ${b})²`, valTiers: tiers,
                code: 'exposant_distribue_sur_la_somme',
            };
        }
        const b = randInt(COEF_MIN, 6);
        const c = randInt(2, BASE_CARRE_MAX);
        const d = randInt(TERME_MIN, Math.min(TERME_MAX, c * c - 1));
        const sansPar = b * c * c - d;                   // b × c² − d
        const surProduit = (b * c) ** 2 - d;             // (b × c)² − d
        const parDiff = b * (c * c - d);                 // b × (c² − d)
        if (![sansPar, surProduit, parDiff].every(dansLesBornes)) continue;
        if (new Set([sansPar, surProduit, parDiff]).size !== 3) continue;
        if (famille === 'produit') {
            return {
                famille, b, c, d,
                exprAvec: `(${b} × ${c})² ${MOINS} ${d}`, valAvec: surProduit,
                exprSans: `${b} × ${puiss(c, 2)} ${MOINS} ${d}`, valSans: sansPar,
                exprTiers: `${b} × (${puiss(c, 2)} ${MOINS} ${d})`, valTiers: parDiff,
                code: 'exposant_sur_le_produit',
            };
        }
        return {
            famille, b, c, d,
            exprAvec: `${b} × (${puiss(c, 2)} ${MOINS} ${d})`, valAvec: parDiff,
            exprSans: `${b} × ${puiss(c, 2)} ${MOINS} ${d}`, valSans: sansPar,
            exprTiers: `(${b} × ${c})² ${MOINS} ${d}`, valTiers: surProduit,
            code: 'parenthese_non_prioritaire',
        };
    }
    // Repli déterministe : 3 × 5² − 7 et (3 × 5)² − 7
    return {
        famille: 'produit', b: 3, c: 5, d: 7,
        exprAvec: '(3 × 5)² − 7', valAvec: 218,
        exprSans: '3 × 5² − 7', valSans: 68,
        exprTiers: '3 × (5² − 7)', valTiers: 54,
        code: 'exposant_sur_le_produit',
    };
};

const genParenthesesTrous = () => {
    const t = tirerParentheses();
    const texte = `<p><b>Calcule A = ${t.exprSans} et B = ${t.exprAvec}.</b> `
        + `Les parenthèses changent tout ! ${CONSIGNE_DECIMALE}</p>`;
    // Une égalité par ligne ; le trou ne contient que des chiffres.
    const phrase = `A = ${t.exprSans} = {{${t.valSans}}}\nB = ${t.exprAvec} = {{${t.valAvec}}}`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PARENTHESES), mode: 'saisie' });
    payload._meta.params = {
        ...t, niveau: 3, nbPuissances: t.famille === 'somme' ? 2 : 1,
        nbOperations: t.famille === 'somme' ? 3 : 3,
        erreurVisee: [t.code],
    };
    return payload;
};

/**
 * La valeur EN TROP du tableau d'association : celle qu'on obtient en lisant le carré
 * comme un double, l'erreur 1 de la spécification.
 *
 * Sans elle, le tableau comptait autant de colonnes que de lignes : l'élève qui avait
 * placé deux valeurs déduisait la troisième par élimination, sans calculer (retour prof
 * 2026-09-25). Une quatrième colonne lui laisse encore deux possibilités devant la
 * dernière ligne, il doit donc aller au bout.
 *
 * @returns {number|null} la valeur leurre, ou null si elle coïncide avec une vraie
 */
const leurreCarreLuCommeDouble = (t) => {
    const v = t.famille === 'somme'
        ? 2 * (t.a + t.b)                     // (a + b)² lu comme 2(a + b)
        : t.b * (2 * t.c) - t.d;              // c² lu comme 2c
    const vraies = new Set([t.valSans, t.valAvec, t.valTiers]);
    return (v > 0 && !vraies.has(v)) ? v : null;
};

const genParenthesesBijection = () => {
    let t = tirerParentheses();
    let leurre = leurreCarreLuCommeDouble(t);
    // Un tirage dont le leurre retomberait sur une vraie valeur est rejoué : la colonne
    // en trop doit être fausse pour TOUTES les lignes.
    for (let essai = 0; essai < 40 && leurre === null; essai++) {
        t = tirerParentheses();
        leurre = leurreCarreLuCommeDouble(t);
    }
    const entrees = shuffle([
        { expr: t.exprSans, val: t.valSans },
        { expr: t.exprAvec, val: t.valAvec },
        { expr: t.exprTiers, val: t.valTiers },
    ]);
    const lignes = entrees.map(e => e.expr);
    const valeurs = entrees.map(e => String(e.val));
    const colonnes = shuffle(leurre === null ? [...valeurs] : [...valeurs, String(leurre)]);
    const bonnesReponses = valeurs.map(v => colonnes.indexOf(v));
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const tableElement = {
        type: 'table',
        config: { type: 'association', colonnes, lignes, bonnesReponses },
    };
    const payload = basePayload('qcm',
        `<p><b>Associe chaque expression à sa valeur.</b> Chaque colonne ne sert qu'une `
        + `fois, et ${leurre === null ? 'toutes servent' : '<b>une valeur est en trop</b>'}. `
        + `${CONSIGNE_DECIMALE}</p>`,
        { choix: [], bonne_reponse: br },
        opts(L_PARENTHESES),
        { version: 1, elements: [tableElement] });
    payload._meta.params = {
        ...t, niveau: 3, nbPuissances: t.famille === 'somme' ? 2 : 1, nbOperations: 3,
        leurre, nbColonnes: colonnes.length,
        erreurVisee: leurre === null ? [t.code] : [t.code, 'puissance_lue_comme_produit'],
    };
    return payload;
};

// ═════════ Carte 3 — Sommes et différences de puissances (niveaux 1 et 4) ═════════

/**
 * niveau 1 : une puissance et une addition ou une soustraction (deux opérations).
 * niveau 4 : deux puissances dont un cube au moins, jusqu'à quatre opérations.
 */
const tirerItemSomme = () => {
    for (let essai = 0; essai < 200; essai++) {
        const type = randomChoice(['sommeSimple', 'difference', 'deuxPuissances', 'deuxPuissancesProduit']);
        if (type === 'sommeSimple') {
            const { c, n, v } = tirerPuissance();
            const a = randInt(TERME_MIN, TERME_MAX);
            const resultat = a + v;
            if (!dansLesBornes(resultat)) continue;
            const faux10 = zerosFaux(c, n);
            return {
                type, niveau: 1, nbPuissances: 1, nbOperations: 2, resultat,
                texte: `${a} + ${puiss(c, n)}`,
                candidats: [
                    { code: 'sans_hierarchie_gauche_droite', valeur: (a + c) ** n },
                    { code: 'puissance_lue_comme_produit', valeur: a + c * n },
                    { code: 'exposant_distribue_sur_la_somme', valeur: a ** n + v },
                    ...(faux10 ? [{ code: 'zeros_de_la_puissance_de_10', valeur: a + faux10 }] : []),
                ],
            };
        }
        if (type === 'difference') {
            const { c, n, v } = tirerPuissance();
            if (v >= TERME_MAX) continue;
            const K = randInt(v + 1, TERME_MAX);
            const resultat = K - v;
            if (!dansLesBornes(resultat)) continue;
            return {
                type, niveau: 1, nbPuissances: 1, nbOperations: 2, resultat,
                texte: `${K} ${MOINS} ${puiss(c, n)}`,
                candidats: [
                    { code: 'puissance_lue_comme_produit', valeur: K - c * n },
                    { code: 'sans_hierarchie_gauche_droite', valeur: (K - c) ** n },
                    { code: 'somme_avant_produit', valeur: K + v },
                ],
            };
        }
        if (type === 'deuxPuissances') {
            const p1 = tirerPuissance(randomChoice([2, 3]));
            const p2 = tirerPuissance(p1.n === 3 ? randomChoice([2, 3]) : 3);
            const resultat = p1.v + p2.v;
            if (!dansLesBornes(resultat)) continue;
            // La puissance de 10 remplacée par sa valeur à un zéro près.
            const faux10 = zerosFaux(p1.c, p1.n) !== null
                ? zerosFaux(p1.c, p1.n) + p2.v
                : (zerosFaux(p2.c, p2.n) !== null ? p1.v + zerosFaux(p2.c, p2.n) : null);
            return {
                type, niveau: 4, nbPuissances: 2, nbOperations: 3, resultat,
                texte: `${puiss(p1.c, p1.n)} + ${puiss(p2.c, p2.n)}`,
                candidats: [
                    { code: 'puissance_lue_comme_produit', valeur: p1.c * p1.n + p2.c * p2.n },
                    { code: 'exposant_distribue_sur_la_somme', valeur: (p1.c + p2.c) ** 2 },
                    { code: 'sans_hierarchie_gauche_droite', valeur: (p1.c + p2.c) ** p1.n },
                    ...(faux10 !== null ? [{ code: 'zeros_de_la_puissance_de_10', valeur: faux10 }] : []),
                ],
            };
        }
        const p1 = tirerPuissance(2);
        const p2 = tirerPuissance(3);
        const b = randInt(COEF_MIN, 5);
        const resultat = p1.v + b * p2.v;
        if (!dansLesBornes(resultat)) continue;
        return {
            type, niveau: 4, nbPuissances: 2, nbOperations: 4, resultat,
            texte: `${puiss(p1.c, 2)} + ${b} × ${puiss(p2.c, 3)}`,
            candidats: [
                { code: 'somme_avant_produit', valeur: (p1.v + b) * p2.v },
                { code: 'puissance_lue_comme_produit', valeur: p1.c * 2 + b * p2.c * 3 },
                { code: 'exposant_sur_le_produit', valeur: p1.v + (b * p2.c) ** 3 },
            ],
        };
    }
    // Repli déterministe : 7 + 2³
    return {
        type: 'sommeSimple', niveau: 1, nbPuissances: 1, nbOperations: 2, resultat: 15,
        texte: '7 + 2³',
        candidats: [
            { code: 'sans_hierarchie_gauche_droite', valeur: 729 },
            { code: 'puissance_lue_comme_produit', valeur: 13 },
            { code: 'exposant_distribue_sur_la_somme', valeur: 351 },
        ],
    };
};

const genSommesTrous = () => {
    for (let essai = 0; essai < 60; essai++) {
        const items = [tirerItemSomme(), tirerItemSomme(), tirerItemSomme()];
        // points_de_vigilance 8 : jamais deux fois la même expression.
        if (new Set(items.map(it => it.texte)).size !== 3) continue;
        const phrase = items.map(it => `${it.texte} = {{${it.resultat}}}`).join('\n');
        const payload = makeTexteTrou(phrase,
            `<p><b>Calcule.</b> ${CONSIGNE_DECIMALE}</p>`,
            { ...opts(L_SOMMES), mode: 'saisie' });
        payload._meta.params = {
            items: items.map(it => it.texte),
            resultats: items.map(it => it.resultat),
            niveau: Math.max(...items.map(it => it.niveau)),
            niveaux: items.map(it => it.niveau),
            nbPuissances: Math.max(...items.map(it => it.nbPuissances)),
            nbOperations: Math.max(...items.map(it => it.nbOperations)),
            erreurVisee: ['puissance_lue_comme_produit'],
        };
        return payload;
    }
    const payload = makeTexteTrou('7 + 2³ = {{15}}\n20 − 3² = {{11}}\n4² + 2³ = {{24}}',
        `<p><b>Calcule.</b> ${CONSIGNE_DECIMALE}</p>`,
        { ...opts(L_SOMMES), mode: 'saisie' });
    payload._meta.params = {
        items: ['7 + 2³', '20 − 3²', '4² + 2³'], resultats: [15, 11, 24],
        niveau: 4, niveaux: [1, 1, 4], nbPuissances: 2, nbOperations: 3,
        erreurVisee: ['puissance_lue_comme_produit'],
    };
    return payload;
};

const genSommesQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const it = tirerItemSomme();
        const distracteurs = distracteursDistincts(it.resultat, it.candidats);
        if (!distracteurs) continue;
        const correct = String(it.resultat);
        const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct,
            `<p><b>Calcule : ${it.texte} = ?</b> ${CONSIGNE_DECIMALE}</p>`, opts(L_SOMMES));
        payload._meta.params = {
            texte: it.texte, resultat: it.resultat, niveau: it.niveau,
            nbPuissances: it.nbPuissances, nbOperations: it.nbOperations, distracteurs,
        };
        return payload;
    }
    const distracteurs = [
        { code: 'sans_hierarchie_gauche_droite', valeur: '729' },
        { code: 'puissance_lue_comme_produit', valeur: '13' },
        { code: 'exposant_distribue_sur_la_somme', valeur: '351' },
    ];
    const payload = makeQcm(['15', ...distracteurs.map(d => d.valeur)], '15',
        `<p><b>Calcule : 7 + 2³ = ?</b> ${CONSIGNE_DECIMALE}</p>`, opts(L_SOMMES));
    payload._meta.params = {
        texte: '7 + 2³', resultat: 15, niveau: 1, nbPuissances: 1, nbOperations: 2, distracteurs,
    };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    etapes_texte_trou_saisie: genEtapesTrous,
    etapes_qcm_unique: genEtapesQcm,
    parentheses_texte_trou_saisie: genParenthesesTrous,
    parentheses_tableau_bijection: genParenthesesBijection,
    sommes_texte_trou_saisie: genSommesTrous,
    sommes_qcm_unique: genSommesQcm,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM',
    tableau_bijection: 'Tableau', texte: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'etapes', label: L_ETAPES, formatsList: buildFormats('etapes') },
    { id: 'parentheses', label: L_PARENTHESES, formatsList: buildFormats('parentheses') },
    { id: 'sommes', label: L_SOMMES, formatsList: buildFormats('sommes') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
